import React, {
  createContext,
  Fragment,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { AnimatePresence, m } from "framer-motion";
import { twJoin } from "tailwind-merge";

import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Icon } from "src/design-system/Icon";

type ControlledProps = {
  value: string[];
  onChange: (value: string[]) => void;
  defaultValue?: never;
};

type UncontrolledProps = {
  defaultValue?: string[];
  onChange?: never;
  value?: never;
};

type AccordionProps = (ControlledProps | UncontrolledProps) & {
  children: React.ReactNode;
  dataLoc?: string;
  loadingSkeleton?: React.ReactNode;
};

type AccordionContextValue = {
  isItemOpen: (value: string) => boolean;
  handleToggle: (value: string) => void;
  isControlled: boolean;
  loadingSkeleton: React.ReactNode;
  dataLoc: string;
};

const AccordionContext = createContext<AccordionContextValue | null>(null);

const useAccordionContext = () => {
  const ctx = useContext(AccordionContext);
  if (!ctx) {
    throw new Error(
      "Accordion.Item must be used within an Accordion component",
    );
  }
  return ctx;
};

// Headless UI's Disclosure manages its own internal open/closed state independently
// per item, but the Accordion coordinates state across all items via React context.
// In controlled mode, external state changes (e.g. single-mode closing a sibling,
// or a disabled item becoming enabled and open) cause the accordion's open state
// to diverge from Disclosure's internal state. DisclosureButton derives aria-expanded
// from Disclosure's internal state and marks it as a controlled prop that cannot be
// overridden via JSX — so we correct it directly on the DOM node via useLayoutEffect
// (before paint, so screen readers never see the stale value).
//
// Only active in controlled mode — in uncontrolled mode Disclosure is the source
// of truth and should not be overridden.
const useDisclosureControlledSync = (
  buttonRef: React.RefObject<HTMLButtonElement | null>,
  open: boolean,
  controlled: boolean,
) => {
  useLayoutEffect(() => {
    if (controlled) {
      buttonRef.current?.setAttribute("aria-expanded", String(open));
    }
  });
};

type AccordionItemProps = {
  value: string;
  label: string;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  rightSlot?: React.ReactNode;
  dataLoc?: string;
  loadingSkeleton?: React.ReactNode;
  isLoading?: boolean;
  forceMount?: boolean;
} & (
  | {
      children: React.ReactNode;
      isDisabled?: boolean;
      isReadOnly?: boolean;
    }
  | {
      children?: never;
      isDisabled?: never;
      isReadOnly?: never;
    }
);

type AccordionItemContentProps = AccordionItemProps & {
  open: boolean;
  controlled: boolean;
  onToggle: (value: string) => void;
};

const AccordionItemContent: React.FC<AccordionItemContentProps> = ({
  value,
  onToggle,
  label,
  icon: IconComponent,
  description,
  children,
  isDisabled,
  isReadOnly,
  rightSlot,
  dataLoc = "accordion-item",
  open,
  controlled,
  loadingSkeleton,
  isLoading,
  forceMount,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useDisclosureControlledSync(buttonRef, open, controlled);

  return (
    <>
      <div className="flex items-start gap-x-2 p-4">
        <DisclosureButton
          ref={buttonRef}
          aria-disabled={isReadOnly || undefined}
          className={twJoin(
            "group flex w-full items-start gap-x-2 text-left focus:outline-none",
            (isDisabled || isReadOnly) && "cursor-default",
          )}
          data-loc={`${dataLoc}-trigger`}
          disabled={isDisabled}
          onClick={isReadOnly ? undefined : () => onToggle(value)}
        >
          <div
            className={twJoin(
              "flex h-5 w-5 shrink-0 items-center justify-center transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
            )}
          >
            <Icon
              icon={faChevronDown}
              size="xs"
              variant={isDisabled ? "disabled" : "hover"}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-start gap-x-2 font-inter-semibold-13px text-gray-800">
              {IconComponent && (
                <span className="flex h-5 shrink-0 items-center">
                  {IconComponent}
                </span>
              )}
              <span className="min-w-0">{label}</span>
            </span>
            {description && (
              <span className="font-inter-medium-12px text-gray-500">
                {description}
              </span>
            )}
          </div>
        </DisclosureButton>
        {rightSlot && (
          <div className="flex h-5 shrink-0 items-center">{rightSlot}</div>
        )}
      </div>
      <AnimatePresence initial={false}>
        {(forceMount || open) && (
          <DisclosurePanel as={Fragment} static>
            <m.div
              key="panel"
              animate={
                open
                  ? {
                      height: "auto",
                      opacity: 1,
                      overflow: "hidden",
                      transitionEnd: { overflow: "visible" },
                    }
                  : { height: 0, opacity: 0, overflow: "hidden" }
              }
              exit={{ height: 0, opacity: 0, overflow: "hidden" }}
              initial={{ height: 0, opacity: 0, overflow: "hidden" }}
              role="region"
              transition={{
                duration: 0.2,
                ease: "easeOut",
              }}
            >
              <div className="pr-6 pb-6 pl-11 font-inter-normal-13px text-gray-800">
                {isLoading ? loadingSkeleton : children}
              </div>
            </m.div>
          </DisclosurePanel>
        )}
      </AnimatePresence>
    </>
  );
};

type AccordionItemStaticProps = Pick<
  AccordionItemProps,
  "label" | "icon" | "description" | "rightSlot" | "dataLoc"
>;

const AccordionItemStatic: React.FC<AccordionItemStaticProps> = ({
  label,
  icon: IconComponent,
  description,
  rightSlot,
  dataLoc = "accordion-item",
}) => {
  return (
    <div
      className={twJoin("flex items-start gap-x-2 p-4")}
      data-loc={`${dataLoc}-trigger`}
    >
      <div className="flex w-full items-start gap-x-2 text-left">
        <div className="h-5 w-5 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-start gap-x-2 font-inter-semibold-13px text-gray-800">
            {IconComponent && (
              <span className="flex h-5 shrink-0 items-center">
                {IconComponent}
              </span>
            )}
            <span className="min-w-0">{label}</span>
          </span>
          {description && (
            <span className="font-inter-medium-12px text-gray-500">
              {description}
            </span>
          )}
        </div>
      </div>
      {rightSlot && (
        <div className="flex h-5 shrink-0 items-center">{rightSlot}</div>
      )}
    </div>
  );
};

const AccordionItem: React.FC<AccordionItemProps> = ({
  value,
  label,
  icon: nodeIcon,
  description,
  children,
  isDisabled,
  isReadOnly,
  rightSlot,
  dataLoc,
  loadingSkeleton: itemLoadingSkeleton,
  isLoading,
  forceMount,
}) => {
  const ctx = useAccordionContext();
  const itemDataLoc = dataLoc ?? `${ctx.dataLoc}-item`;
  const skeleton = itemLoadingSkeleton ?? ctx.loadingSkeleton;

  const rawOpen = ctx.isItemOpen(value);
  const effectiveOpen = isDisabled ? false : rawOpen;

  if (children != null) {
    return (
      <Disclosure as="div" data-loc={itemDataLoc} defaultOpen={effectiveOpen}>
        <AccordionItemContent
          controlled={ctx.isControlled}
          dataLoc={dataLoc}
          description={description}
          forceMount={forceMount}
          icon={nodeIcon}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isReadOnly={isReadOnly}
          label={label}
          loadingSkeleton={skeleton}
          open={effectiveOpen}
          rightSlot={rightSlot}
          value={value}
          onToggle={ctx.handleToggle}
        >
          {children}
        </AccordionItemContent>
      </Disclosure>
    );
  }

  return (
    <div data-loc={itemDataLoc}>
      <AccordionItemStatic
        dataLoc={dataLoc}
        description={description}
        icon={nodeIcon}
        label={label}
        rightSlot={rightSlot}
      />
    </div>
  );
};

const AccordionSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-y-3">
      <SkeletonPlaceholder height="h-6" width="w-full" />
      <SkeletonPlaceholder height="h-6" width="w-3/4" />
      <SkeletonPlaceholder height="h-6" width="w-5/6" />
    </div>
  );
};
export const Accordion: React.FC<AccordionProps> & {
  Item: typeof AccordionItem;
} = ({
  children,
  dataLoc = "accordion",
  loadingSkeleton = <AccordionSkeleton />,
  ...props
}) => {
  const { defaultValue, onChange, value } = props;
  const isControlled = value !== undefined;

  const [internalOpen, setInternalOpen] = useState<string[]>(
    () => defaultValue ?? [],
  );

  const isItemOpen = (itemValue: string): boolean => {
    if (isControlled) {
      return value.includes(itemValue);
    }
    return internalOpen.includes(itemValue);
  };

  const handleToggle = (val: string) => {
    if (isControlled) {
      const newValue = value.includes(val)
        ? value.filter((v) => v !== val)
        : [...value, val];
      onChange(newValue);
    } else {
      setInternalOpen((prev) =>
        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
      );
    }
  };

  const contextValue: AccordionContextValue = {
    isItemOpen,
    handleToggle,
    isControlled,
    loadingSkeleton,
    dataLoc,
  };

  return (
    <div
      className="divide-y divide-gray-200 rounded-lg border border-gray-200"
      data-loc={dataLoc}
    >
      <AccordionContext.Provider value={contextValue}>
        {children}
      </AccordionContext.Provider>
    </div>
  );
};

Accordion.Item = AccordionItem;

/**
 * Hook that provides controlled state for single-mode accordion behavior
 * (at most one item open at a time).
 *
 * Usage:
 *   const { value, onChange } = useAccordionSingleMode("item-1");
 *   <Accordion value={value} onChange={onChange}>...</Accordion>
 */
export const useAccordionSingleMode = (defaultValue?: string) => {
  const [value, setValue] = useState<string[]>(
    defaultValue !== undefined ? [defaultValue] : [],
  );

  const onChange = (next: string[]) => {
    const opened = next.filter((v) => !value.includes(v));
    setValue(opened.length > 0 ? [opened[0]] : []);
  };

  return { value, onChange } as const;
};
